import Axios, { VirtualDeviceApi } from '@/api';

/**
 * Collect ports on this serial that are bound to a virtual button or curtain (scene).
 * @returns {Set<number>}
 */
function collectBoundScenePorts(serial, zones) {
  const bound = new Set();
  for (const zone of zones || []) {
    for (const g of zone.groups || []) {
      for (const d of g.devices || []) {
        if (!d?.boundButton) continue;
        // 가상버튼 또는 커튼(type 3/5/6) boundButton → scene
        const isCurtain =
          !d.isVirtual && (d.type === 3 || d.type === 5 || d.type === 6);
        if (!d.isVirtual && !isCurtain) continue;
        if (String(d.boundButton.controllerId) !== String(serial)) continue;
        const p = Number(d.boundButton.port);
        if (Number.isInteger(p) && p >= 0 && p <= 11) bound.add(p);
      }
    }
  }
  return bound;
}

/**
 * Build cmd "bm" maps for one controller from zone devices + VirtualDevice mappingType=1.
 * Same-controller ports only; default 1:1. cross[] for other-controller targets (server-aligned).
 * Ports with boundButton → { scene: true } (로컬 토글 없이 서버 씬).
 */
export async function buildButtonMapsForController(serial, zones) {
  if (!serial) return [];

  const scenePorts = collectBoundScenePorts(serial, zones);

  const devices = [];
  for (const zone of zones || []) {
    for (const g of zone.groups || []) {
      for (const d of g.devices || []) {
        if (d?.controllerId === serial && d?.type === 1 && !d?.isVirtual) {
          devices.push(d);
        }
      }
    }
  }

  const maps = [];
  const covered = new Set();

  for (const device of devices) {
    const rawPorts = device.item?.ports;
    const sourcePorts = Array.isArray(rawPorts)
      ? rawPorts.map((p) => Number(p)).filter((p) => Number.isInteger(p))
      : rawPorts != null && Number.isInteger(Number(rawPorts))
        ? [Number(rawPorts)]
        : [];
    if (sourcePorts.length === 0) continue;
    const sourcePort = sourcePorts[0];

    // 가상버튼에 바인딩된 포트는 물리 1:1/매핑 대신 scene 엔트리만
    if (scenePorts.has(sourcePort)) {
      continue;
    }

    const sameBoard = new Set([sourcePort]);
    const crossBySerial = new Map();

    try {
      const result = await Axios.get(
        VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', device._id),
        {},
        '',
        { hideLoading: true },
      );
      const vd = result?.resultData;
      if (result?.resultCode === '0000' && vd?.mappingType === 1) {
        const items = Array.isArray(vd.items) ? vd.items : Object.values(vd.items || {});
        for (const it of items) {
          if (!it || !it.controllerId) continue;
          const ports = Array.isArray(it.item?.ports)
            ? it.item.ports.map((p) => Number(p)).filter((p) => Number.isInteger(p))
            : it.item?.ports != null && Number.isInteger(Number(it.item.ports))
              ? [Number(it.item.ports)]
              : [];
          const validPorts = ports.filter((p) => p >= 0 && p <= 11);
          if (validPorts.length === 0) continue;

          if (it.controllerId === serial) {
            for (const p of validPorts) {
              if (!scenePorts.has(p)) sameBoard.add(p);
            }
            continue;
          }

          const prev = crossBySerial.get(it.controllerId) || {
            serial: it.controllerId,
            ports: new Set(),
            bright: it.item?.bright != null ? Number(it.item.bright) : 100,
            color: it.item?.color != null ? Number(it.item.color) : 10,
          };
          for (const p of validPorts) prev.ports.add(p);
          crossBySerial.set(it.controllerId, prev);
        }
      }
    } catch {
      /* keep 1:1 */
    }

    const entry = {
      port: sourcePort,
      ports: [...sameBoard].sort((a, b) => a - b),
    };
    if (crossBySerial.size > 0) {
      entry.cross = [...crossBySerial.values()].map((c) => ({
        serial: c.serial,
        ports: [...c.ports].sort((a, b) => a - b),
        bright: Number.isFinite(c.bright) ? c.bright : 100,
        color: Number.isFinite(c.color) ? c.color : 10,
      }));
    }
    maps.push(entry);
    covered.add(sourcePort);
  }

  // 흡수로 물리 Device가 없어도 bound 포트는 scene 엔트리 필요
  for (const port of [...scenePorts].sort((a, b) => a - b)) {
    if (covered.has(port)) continue;
    maps.push({ port, ports: [port], scene: true });
    covered.add(port);
  }

  return maps;
}

export function publishButtonMapsPayload(mqttControl, serial, maps) {
  if (!mqttControl || !serial || !Array.isArray(maps) || maps.length === 0) return;
  mqttControl.onPublishController(serial, {
    serial,
    cmd: 'bm',
    data: { maps },
  });
}
